import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import Header from '../components/Header';
import MenuBar from '../components/MenuBar';
import ProtectedRoute from '../components/ProtectedRoute';
import Report from '../components/Report';
import { useInformationQuery } from '../features/settings';
import { featuresContext, temperatureContext } from '../lib/context';
import './Common.css';
import DownloadPage from './Download';
import Forgot from './Forgot';
import Login from './Login';
import OverviewPage from './Overview';
import PendingPage from './Pending';
import RealtimePage from './Realtime';
import RecordsPage from './Records';
import Reset from './Reset';
import SettingPage from './Setting';
import Signup from './Signup';
import Verify from './Verify';

const NO_FEATURES: string[] = [];

function Home() {
    const isClient = typeof window !== 'undefined';

    const { t, i18n } = useTranslation();
    const [color] = useState('light');
    const [companyName, setCompanyName] = useState('');
    const [login, setLogin] = useState(() => localStorage.getItem('token') !== null);
    const [isMobile, setIsMobile] = useState(isClient ? window.innerWidth <= 768 : false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [desktopExpanded, setDesktopExpanded] = useState(false);
    // Public routes render without a session; a token ProtectedRoute adopts later is fetched by the MenuBar.
    const { data: account } = useInformationQuery({ enabled: login });
    const tempUnit = account?.preference?.temp_unit || 'celsius';
    const features = account?.features ?? NO_FEATURES;
    const language = account?.preference?.language;
    const appliedLangTokenRef = useRef<string | null>(null);

    // Once per login, so it doesn't undo a manual switch (e.g. on the public Download page).
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!language || !token || appliedLangTokenRef.current === token) return;
        i18n.changeLanguage(language);
        appliedLangTokenRef.current = token;
    }, [language, i18n]);

    useEffect(() => {
        if (!isClient) {
            return;
        }
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isClient]);

    useEffect(() => {
        if (!isMobile) {
            setMobileMenuOpen(false);
        } else {
            setDesktopExpanded(false);
        }
    }, [isMobile]);

    const homeStyle = useMemo(
        () => ({
            width: '100%',
            minHeight: '100vh',
            backgroundColor: color === 'light' ? '#F3F5F9' : '#262729',
        }),
        [color]
    );

    const contentStyle = useMemo(
        () =>
            isMobile
                ? { marginLeft: 0, paddingBottom: '40px' }
                : {
                      marginLeft: desktopExpanded ? '320px' : '80px',
                      paddingBottom: '40px',
                      transition: 'margin-left 300ms ease',
                  },
        [isMobile, desktopExpanded]
    );

    const pageWrapperStyle = useMemo(() => (isMobile ? { marginTop: '16px' } : { marginTop: '24px' }), [isMobile]);

    const handleMenuToggle = () => {
        setMobileMenuOpen((prev: any) => !prev);
    };

    const handleMenuClose = () => {
        setMobileMenuOpen(false);
    };

    const handleDesktopMenuToggle = () => {
        setDesktopExpanded((prev: any) => !prev);
    };

    const renderWithShell = (title: any, element: any) => (
        <ProtectedRoute>
            <div>
                <MenuBar
                    color={color}
                    isMobile={isMobile}
                    desktopExpanded={desktopExpanded}
                    onDesktopToggle={handleDesktopMenuToggle}
                    open={isMobile ? mobileMenuOpen : undefined}
                    onClose={handleMenuClose}
                />
                <div style={contentStyle}>
                    <Header
                        title={title}
                        companyName={companyName}
                        setLogin={setLogin}
                        color={color}
                        isMobile={isMobile}
                        onMenuToggle={handleMenuToggle}
                    />
                    <div style={pageWrapperStyle}>{element}</div>
                </div>
            </div>
        </ProtectedRoute>
    );

    return (
        <div className='Home' style={homeStyle}>
            <featuresContext.Provider value={features}>
                <temperatureContext.Provider value={tempUnit}>
                    <BrowserRouter>
                        <Routes>
                            <Route path='/' element={<Navigate to='/overview' />} />
                            <Route
                                path='/login'
                                element={<Login setCompanyName={setCompanyName} setLogin={setLogin} />}
                            />
                            <Route
                                path='/forgot'
                                element={<Forgot setCompanyName={setCompanyName} setLogin={setLogin} />}
                            />
                            <Route
                                path='/signup'
                                element={<Signup setCompanyName={setCompanyName} setLogin={setLogin} />}
                            />
                            <Route path='/verify' element={<Verify />} />
                            <Route path='/reset' element={<Reset />} />
                            <Route
                                path='/overview'
                                element={renderWithShell(t('Overview.title'), <OverviewPage isMobile={isMobile} />)}
                            />
                            <Route path='/pending' element={renderWithShell(t('Pending.title'), <PendingPage />)} />
                            <Route path='/uploaded' element={renderWithShell(t('Records.title'), <RecordsPage />)} />
                            <Route
                                path='/realtime'
                                element={renderWithShell(
                                    t('RealTime.title'),
                                    <RealtimePage isVendor={!!account?.is_vendor} />
                                )}
                            />
                            <Route path='/setting' element={renderWithShell(t('Information.title'), <SettingPage />)} />
                            <Route path='/report' element={renderWithShell('報告 Report', <Report />)} />
                            <Route path='/download' element={<DownloadPage />} />
                        </Routes>
                    </BrowserRouter>
                </temperatureContext.Provider>
            </featuresContext.Provider>
        </div>
    );
}

export default Home;
